import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Banknote, Clock, Shield, Users, CheckCircle, Calculator, FileText, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function CoopLoansPage() {
  const navigate = useNavigate();

  const loanTypes = [
    {
      title: 'Emergency Loan',
      amount: 'Up to ₦500,000',
      rate: '2% monthly',
      tenure: '1-6 months',
      description: 'Quick access to funds for urgent needs',
      icon: <Clock className="w-6 h-6" />
    },
    {
      title: 'Business Loan',
      amount: 'Up to ₦2,000,000',
      rate: '1.8% monthly',
      tenure: '6-24 months',
      description: 'Grow your business with flexible capital',
      icon: <TrendingUp className="w-6 h-6" />
    },
    {
      title: 'Asset Loan',
      amount: 'Up to ₦5,000,000',
      rate: '1.5% monthly',
      tenure: '12-36 months',
      description: 'Finance equipment, vehicles, or property',
      icon: <Banknote className="w-6 h-6" />
    }
  ];

  const benefits = [
    { icon: <Clock className="w-6 h-6" />, title: 'Quick Approval', desc: 'Get approved within 24 hours' },
    { icon: <Shield className="w-6 h-6" />, title: 'No Collateral', desc: 'Your savings serve as guarantee' },
    { icon: <Users className="w-6 h-6" />, title: 'Group Support', desc: 'Cooperative members vouch for you' },
    { icon: <Calculator className="w-6 h-6" />, title: 'Low Interest', desc: 'Rates as low as 1.5% monthly' }
  ];

  const requirements = [
    'Active cooperative membership for at least 3 months',
    'Minimum savings balance of 20% of loan amount',
    'Valid government-issued ID',
    'Proof of income or business registration',
    'Two guarantors from your cooperative group'
  ];

  const steps = [
    { step: '01', title: 'Apply Online', desc: 'Fill out the loan application form in the app' },
    { step: '02', title: 'Get Verified', desc: 'We verify your membership and savings history' },
    { step: '03', title: 'Approval', desc: 'Receive approval within 24 hours' },
    { step: '04', title: 'Disbursement', desc: 'Funds credited directly to your wallet' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-[#221a83]/5 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-indigo-50 text-brand-blue px-4 py-2 rounded-full text-sm font-medium">
                <Banknote className="w-4 h-4" />
                <span>Cooperative Loans</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold font-heading text-slate-900 leading-tight">
                Quick Loans For Cooperative Members
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Access affordable loans backed by your cooperative savings. No collateral required, 
                competitive rates, and fast approval within 24 hours.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/auth')}
                  className="bg-brand-blue text-white hover:bg-[#1a1466] rounded-full px-8 py-6 text-lg font-semibold"
                >
                  Apply For Loan
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/server-circle')}
                  className="rounded-full px-8 py-6 text-lg"
                >
                  Join Cooperative
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?q=80&w=2071&auto=format&fit=crop"
                alt="Cooperative Loans"
                className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-[250px]">
                <p className="text-sm text-slate-600 mb-1">Loan Approved!</p>
                <p className="text-2xl font-bold text-brand-blue">₦1,500,000</p>
                <p className="text-xs text-slate-500 mt-1">Disbursed in 24 hours</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Loan Types Section */}
      <section className="py-20 px-4 md:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              Loan Options For Every Need
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose from our range of cooperative loan products designed to meet your financial needs
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {loanTypes.map((loan, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200 hover:border-brand-blue/50 transition-all hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-brand-blue flex items-center justify-center mb-6">
                  {loan.icon}
                </div>
                <h3 className="text-xl font-bold font-heading text-slate-900 mb-2">{loan.title}</h3>
                <p className="text-slate-600 mb-4">{loan.description}</p>
                <div className="space-y-2 pt-4 border-t border-slate-100">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Amount</span>
                    <span className="font-semibold text-slate-900">{loan.amount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Interest Rate</span>
                    <span className="font-semibold text-brand-blue">{loan.rate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Tenure</span>
                    <span className="font-semibold text-slate-900">{loan.tenure}</span>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/auth')}
                  className="w-full mt-6 bg-brand-blue text-white hover:bg-[#1a1466] rounded-full"
                >
                  Apply Now
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-6">
                Why Choose Britedge Cooperative Loans?
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Our cooperative lending model ensures you get the best rates and fastest approvals 
                while building financial solidarity with your community.
              </p>
              <div className="grid grid-cols-2 gap-6">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 text-brand-blue flex items-center justify-center flex-shrink-0">
                      {benefit.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">{benefit.title}</h4>
                      <p className="text-sm text-slate-600">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2070&auto=format&fit=crop"
                alt="Benefits"
                className="rounded-3xl shadow-xl w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 md:px-8 bg-gradient-to-br from-[#221a83] to-[#1a1466] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading mb-4">
              How To Get A Loan
            </h2>
            <p className="text-lg text-indigo-100 max-w-2xl mx-auto">
              Simple 4-step process to access funds quickly
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                <p className="text-indigo-100">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <img 
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2072&auto=format&fit=crop"
                alt="Requirements"
                className="rounded-3xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 bg-indigo-50 text-brand-blue px-4 py-2 rounded-full text-sm font-medium mb-4">
                <FileText className="w-4 h-4" />
                <span>Requirements</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-6">
                What You Need To Apply
              </h2>
              <ul className="space-y-4">
                {requirements.map((req, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-brand-blue flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{req}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate('/auth')}
                className="mt-8 bg-brand-blue text-white hover:bg-[#1a1466] rounded-full px-8 py-6 text-lg font-semibold"
              >
                Start Application
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
            Ready To Get Started?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join a cooperative group today and unlock access to affordable loans
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/auth')}
              className="bg-brand-blue text-white hover:bg-[#1a1466] rounded-full px-10 py-6 text-lg font-semibold"
            >
              Apply For Loan
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/server-circle')}
              className="rounded-full px-10 py-6 text-lg"
            >
              Join Cooperative First
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p>&copy; 2024 Britedge Financial Services. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
